import {FC, ReactNode, useRef} from 'react'
import {useParams} from 'react-router-dom'
import {appsAPI} from '../../../../../services/AppsService'
import {IBitrix24AppCheck, IBitrix24AppCheckRequest} from '../../../../../types/apps/crm/biitrix24/Bitrix24AppCheckModel'
import {useIntl} from 'react-intl'

const badgeClass = {
  OK: 'badge-light-success',
  NOT_INSTALLED: 'badge-light-danger',
  WRONG_APP: 'badge-light-danger',
  ERROR: 'badge-light-warning',
}

type Props = {
  children?: ReactNode
  modeIsCloud?: boolean
  credentials?: IBitrix24AppCheckRequest['credentials']
  hideHint?: boolean
}

const Bitrix24AppCheck: FC<Props> = ({children, modeIsCloud, credentials, hideHint}) => {
  const intl = useIntl()
  const {appId} = useParams()
  const {data: app} = appsAPI.useFetchAppByIdQuery(appId!)
  const {
    data: check,
    isFetching,
    isError,
    refetch,
  } = appsAPI.useCheckBitrix24AppQuery(
    {appId: appId!, modeIsCloud, credentials},
    {refetchOnMountOrArgChange: true}
  )
  const lastState = useRef<IBitrix24AppCheck['state']>()
  if (!isFetching) lastState.current = isError ? 'ERROR' : check?.state
  const state = lastState.current
  const showHint = !hideHint && (state === 'NOT_INSTALLED' || state === 'WRONG_APP')
  const isCloud = modeIsCloud ?? app?.settings.bitrix24.mode_is_cloud

  return (
    <>
      <div className='d-flex flex-stack'>
        {children}
        <div className='d-flex align-items-center'>
          <span
            className='spinner-border spinner-border-sm text-primary me-3'
            style={{visibility: isFetching ? 'visible' : 'hidden'}}
          />
          {state && (
            <span className={`badge ${badgeClass[state]} me-3`}>
              {intl.formatMessage({id: `APPS.BITRIX24.CHECK.${state}`})}
            </span>
          )}
          <button className='btn btn-sm btn-light-primary py-1 px-3' onClick={refetch} disabled={isFetching}>
            {intl.formatMessage({id: 'APPS.BITRIX24.CHECK.BUTTON'})}
          </button>
        </div>
      </div>
      {showHint && (
        <div className='text-gray-600 fs-7 mt-2'>
          {intl.formatMessage({
            id: isCloud ? 'APPS.BITRIX24.CHECK.HINT.CLOUD' : 'APPS.BITRIX24.CHECK.HINT.LOCAL',
          })}
        </div>
      )}
    </>
  )
}

export {Bitrix24AppCheck}
